import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  FormattedPlanDetails,
  LanguageNameSpaces,
  SubscriptionDuration,
  SubscriptionPlanType,
} from "@shared/types";
import { useUser } from "@shared/utils/hooks/misc.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import NewLookPlanCard from "features/settings/Subscription/NewLook/NewLookPlanCard.tsx";

interface NewLookPlanCarouselProps {
  plans: FormattedPlanDetails[];
  isUpgrade?: boolean;
  promoCodeDiscount?: Record<SubscriptionDuration, number>;
}

/** Side padding of the strip; matches the phone page gutter. */
const SIDE = 16;
const GAP = 12;

/**
 * Phone: the regular plans as a horizontal swipe carousel. Each card is
 * ~84% of the viewport so the next one peeks in; dots under it follow the
 * scroll and jump to a card on tap. Opens on the current plan (or Premium).
 */
const NewLookPlanCarousel = ({
  plans,
  isUpgrade,
  promoCodeDiscount,
}: NewLookPlanCarouselProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { user } = useUser();
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const getCards = () =>
    Array.from(trackRef.current?.children ?? []) as HTMLElement[];

  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = "smooth") => {
      const track = trackRef.current;
      const card = getCards()[index];
      if (!track || !card) return;
      track.scrollTo({ left: card.offsetLeft - SIDE, behavior });
    },
    []
  );

  // Open on the user's current plan if it is one of these, else Premium.
  const initialType = plans.some(
    (plan) => plan.subscription_plan_type === user?.subscription_plan_type
  )
    ? user?.subscription_plan_type
    : SubscriptionPlanType.PREMIUM;
  const initialIndex = Math.max(
    0,
    plans.findIndex((plan) => plan.subscription_plan_type === initialType)
  );
  useEffect(() => {
    setActive(initialIndex);
    // Wait a frame so the cards have laid out.
    const frame = requestAnimationFrame(() =>
      scrollToIndex(initialIndex, "auto")
    );
    return () => cancelAnimationFrame(frame);
  }, [initialIndex, plans.length, scrollToIndex]);

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const cards = getCards();
    let nearest = 0;
    let best = Infinity;
    cards.forEach((card, index) => {
      const distance = Math.abs(card.offsetLeft - SIDE - track.scrollLeft);
      if (distance < best) {
        best = distance;
        nearest = index;
      }
    });
    if (nearest !== active) setActive(nearest);
  };

  return (
    <Wrapper>
      <Track ref={trackRef} onScroll={onScroll}>
        {plans.map((plan) => (
          <Slide key={plan.subscription_plan_type}>
            <NewLookPlanCard
              plan={plan}
              isUpgrade={isUpgrade}
              promoCodeDiscount={promoCodeDiscount}
            />
          </Slide>
        ))}
      </Track>
      <Dots role="tablist">
        {plans.map((plan, index) => (
          <Dot
            key={plan.subscription_plan_type}
            type="button"
            role="tab"
            aria-selected={index === active}
            aria-label={
              plan.subscription_type || t("choose_plan.choose_your_plan")
            }
            $active={index === active}
            onClick={() => {
              setActive(index);
              scrollToIndex(index);
            }}
          />
        ))}
      </Dots>
    </Wrapper>
  );
};

export default NewLookPlanCarousel;

const Wrapper = styled.div`
  /* Bleed to the screen edges so cards can scroll under the page gutter. */
  width: calc(100% + ${2 * SIDE}px);
  margin: 0 -${SIDE}px;
`;
const Track = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${GAP}px;
  padding: 2px ${SIDE}px 4px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scroll-padding: 0 ${SIDE}px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const Slide = styled.div`
  flex: 0 0 84vw;
  max-width: 340px;
  scroll-snap-align: start;
  display: flex;
  > * {
    flex: 1;
  }
`;
const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 2px;
  margin-top: 4px;
`;
/** A 6px dot inside a 24px hit area; the active one stretches to a bar. */
const Dot = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  &::before {
    content: "";
    width: ${(p) => (p.$active ? 16 : 6)}px;
    height: 6px;
    border-radius: 3px;
    background: ${(p) =>
      p.$active
        ? getNewLookTokens(p.theme).accent
        : getNewLookTokens(p.theme).hairlineStrong};
    transition: width 150ms, background 150ms;
  }
`;
